/**
 * stock-listings.js
 * RVLife Stock Listings — Client-side filter, search, sort & UI logic
 *
 * Included via layout/theme.liquid:
 *   {{ 'stock-listings.js' | asset_url | script_tag }}
 *
 * Functions exposed globally (used inline in Liquid templates):
 *   rvlOpenEnquiry(title, url)
 *   rvlCloseEnquiry()
 *   rvlClearFilters()
 *   rvlToggleInetPrice(checkbox)  — submit form
 *   rvlDeleteListing(idx)          — dashboard
 *   rvlUnsave(url)                 — dashboard
 */

(function () {
  'use strict';

  /* ============================================================
     UTILITIES
     ============================================================ */

  /**
   * Debounce: delays fn until after wait ms have elapsed since last call.
   */
  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(this, args); }, wait);
    };
  }

  /**
   * Normalise a string for comparison (lowercase, trim).
   */
  function norm(str) {
    return (str || '').toString().toLowerCase().trim();
  }

  /* ============================================================
     ENQUIRY MODAL
     ============================================================ */

  /**
   * Open the enquiry modal and pre-fill listing title / URL.
   * Called from listing card buttons and detail page.
   *
   * @param {string} title  - Listing title
   * @param {string} url    - Listing URL (relative)
   */
  window.rvlOpenEnquiry = function (title, url) {
    var modal = document.getElementById('rvl-enquiry-modal');
    var nameEl = document.getElementById('rvl-modal-listing-name');
    var urlEl  = document.getElementById('rvl-modal-listing-url');

    if (!modal) return;

    if (nameEl) nameEl.textContent = title || '';
    if (urlEl)  urlEl.value = url || '';

    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    // Focus first input for accessibility
    var firstInput = modal.querySelector('input[type="text"], input[type="email"]');
    if (firstInput) {
      setTimeout(function () { firstInput.focus(); }, 50);
    }
  };

  /**
   * Close the enquiry modal.
   */
  window.rvlCloseEnquiry = function () {
    var modal = document.getElementById('rvl-enquiry-modal');
    if (!modal) return;
    modal.style.display = 'none';
    document.body.style.overflow = '';
  };

  // Close modal on Escape key
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { window.rvlCloseEnquiry(); }
  });

  /* ============================================================
     STOCK LISTINGS PAGE — FILTER, SEARCH & SORT
     Only runs on pages containing #rvl-grid
     ============================================================ */

  var grid = document.getElementById('rvl-grid');
  if (grid) {
    initListingsPage();
  }

  function initListingsPage() {

    /* ---- References ---- */
    var searchInput   = document.getElementById('rvl-search');
    var sortSelect    = document.getElementById('rvl-sort');
    var countEl       = document.getElementById('rvl-count');
    var noResults     = document.getElementById('rvl-no-results');
    var applyBtn      = document.getElementById('rvl-apply-filters');
    var clearBtn      = document.getElementById('rvl-clear-filters');

    var filterCategory = document.getElementById('filter-category');
    var filterMake     = document.getElementById('filter-make');
    var filterYearMin  = document.getElementById('filter-year-min');
    var filterYearMax  = document.getElementById('filter-year-max');
    var filterPriceMin = document.getElementById('filter-price-min');
    var filterPriceMax = document.getElementById('filter-price-max');

    /* ---- Collect all product cards ---- */
    // Each card rendered by stock-listing-card.liquid carries data-* attributes
    var allCards = Array.from(grid.querySelectorAll('.rvl-card'));

    /* ---- State ---- */
    var activeFilters = {
      search:    '',
      category:  '',
      make:      '',
      yearMin:   '',
      yearMax:   '',
      priceMin:  '',
      priceMax:  '',
      condition: ['new', 'used', 'demo'],
      type:      ['dealer', 'member']
    };

    /* ---- Read filter values from DOM ---- */
    function readFilters() {
      activeFilters.search   = norm(searchInput ? searchInput.value : '');
      activeFilters.category = norm(filterCategory ? filterCategory.value : '');
      activeFilters.make     = norm(filterMake ? filterMake.value : '');
      activeFilters.yearMin  = filterYearMin ? filterYearMin.value : '';
      activeFilters.yearMax  = filterYearMax ? filterYearMax.value : '';
      activeFilters.priceMin = filterPriceMin ? filterPriceMin.value : '';
      activeFilters.priceMax = filterPriceMax ? filterPriceMax.value : '';

      // Condition checkboxes
      activeFilters.condition = Array.from(
        document.querySelectorAll('[data-filter-check="condition"]:checked')
      ).map(function (el) { return norm(el.value); });

      // Type checkboxes
      activeFilters.type = Array.from(
        document.querySelectorAll('[data-filter-check="type"]:checked')
      ).map(function (el) { return norm(el.value); });
    }

    /* ---- Test a single card against active filters ---- */
    function cardMatches(card) {
      var category  = norm(card.dataset.category  || '');
      var make      = norm(card.dataset.make       || '');
      var year      = parseInt(card.dataset.year   || '0', 10);
      var price     = parseInt(card.dataset.price  || '0', 10);
      var condition = norm(card.dataset.condition  || '');
      var type      = norm(card.dataset.type       || '');
      var searchText = norm(card.dataset.search    || card.textContent);

      // Search
      if (activeFilters.search && searchText.indexOf(activeFilters.search) === -1) {
        return false;
      }

      // Category
      if (activeFilters.category && category !== activeFilters.category) {
        return false;
      }

      // Make
      if (activeFilters.make && make !== activeFilters.make) {
        return false;
      }

      // Year range
      if (activeFilters.yearMin && year < parseInt(activeFilters.yearMin, 10)) {
        return false;
      }
      if (activeFilters.yearMax && year > parseInt(activeFilters.yearMax, 10)) {
        return false;
      }

      // Price range
      if (activeFilters.priceMin && price < parseInt(activeFilters.priceMin, 10)) {
        return false;
      }
      if (activeFilters.priceMax && price > parseInt(activeFilters.priceMax, 10)) {
        return false;
      }

      // Condition — card condition may be 'used_good' or 'used_fair', both map to 'used'
      var conditionGroup = condition.startsWith('used') ? 'used' : condition;
      if (activeFilters.condition.length && activeFilters.condition.indexOf(conditionGroup) === -1) {
        return false;
      }

      // Listing type
      if (activeFilters.type.length && activeFilters.type.indexOf(type) === -1) {
        return false;
      }

      return true;
    }

    /* ---- Sort the visible cards ---- */
    function sortCards(cards) {
      var sortBy = sortSelect ? sortSelect.value : 'newest';

      return cards.slice().sort(function (a, b) {
        switch (sortBy) {
          case 'price-asc':
            return (parseInt(a.dataset.price, 10) || 0) - (parseInt(b.dataset.price, 10) || 0);
          case 'price-desc':
            return (parseInt(b.dataset.price, 10) || 0) - (parseInt(a.dataset.price, 10) || 0);
          case 'year-desc':
            return (parseInt(b.dataset.year, 10) || 0) - (parseInt(a.dataset.year, 10) || 0);
          case 'newest':
          default:
            // Use DOM order (Shopify sorts by published_at by default)
            return (parseInt(a.dataset.index, 10) || 0) - (parseInt(b.dataset.index, 10) || 0);
        }
      });
    }

    /* ---- Apply filters + sort, then update DOM ---- */
    function applyFilters() {
      readFilters();

      var matched   = allCards.filter(cardMatches);
      var unmatched = allCards.filter(function (c) { return !cardMatches(c); });

      // Sort matched cards
      var sorted = sortCards(matched);

      // Re-append sorted matched cards, hide unmatched
      sorted.forEach(function (card) {
        card.style.display = '';
        grid.appendChild(card); // moves card to end in sorted order
      });
      unmatched.forEach(function (card) {
        card.style.display = 'none';
      });

      // Update count
      if (countEl) countEl.textContent = matched.length;
      var labelEl = document.getElementById('rvl-count-label');
      if (labelEl) labelEl.textContent = matched.length === 1 ? 'listing' : 'listings';

      // Show/hide no-results panel
      if (noResults) {
        noResults.style.display = matched.length === 0 ? 'block' : 'none';
      }
    }

    /* ---- Clear all filters ---- */
    window.rvlClearFilters = function () {
      if (searchInput)   searchInput.value   = '';
      if (filterCategory) filterCategory.value = '';
      if (filterMake)    filterMake.value    = '';
      if (filterYearMin) filterYearMin.value = '';
      if (filterYearMax) filterYearMax.value = '';
      if (filterPriceMin) filterPriceMin.value = '';
      if (filterPriceMax) filterPriceMax.value = '';

      document.querySelectorAll('[data-filter-check]').forEach(function (el) {
        el.checked = true;
      });

      applyFilters();
    };

    /* ---- URL param: pre-select a filter on page load ---- */
    function applyUrlParams() {
      var params = new URLSearchParams(window.location.search);
      var catParam = params.get('filter_category');
      if (catParam && filterCategory) {
        filterCategory.value = catParam;
      }
      var makeParam = params.get('filter_make');
      if (makeParam && filterMake) {
        filterMake.value = makeParam;
      }
      var typeParam = params.get('filter_type');
      if (typeParam) {
        document.querySelectorAll('[data-filter-check="type"]').forEach(function (el) {
          el.checked = (norm(el.value) === norm(typeParam));
        });
      }
    }

    /* ---- Mobile filter sidebar toggle ---- */
    var filterToggleBtn = document.getElementById('rvl-filter-toggle');
    var filterSidebar   = document.getElementById('rvl-filters');
    if (filterToggleBtn && filterSidebar) {
      filterToggleBtn.addEventListener('click', function () {
        filterSidebar.classList.toggle('rvl-filters--open');
      });
    }

    /* ---- Event listeners ---- */
    if (applyBtn) {
      applyBtn.addEventListener('click', applyFilters);
    }
    if (clearBtn) {
      clearBtn.addEventListener('click', window.rvlClearFilters);
    }
    if (sortSelect) {
      sortSelect.addEventListener('change', applyFilters);
    }

    // Live search (debounced)
    if (searchInput) {
      searchInput.addEventListener('input', debounce(applyFilters, 300));
    }

    // Filter inputs — apply on Enter key in range fields
    [filterYearMin, filterYearMax, filterPriceMin, filterPriceMax].forEach(function (el) {
      if (el) {
        el.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') { applyFilters(); }
        });
      }
    });

    /* ---- Assign index for stable sort-by-newest ---- */
    allCards.forEach(function (card, i) {
      card.dataset.index = i;
    });

    /* ---- Init ---- */
    applyUrlParams();
    applyFilters();
  }

  /* ============================================================
     SUBMIT FORM — INTERNET PRICE TOGGLE
     ============================================================ */

  /**
   * Show/hide the Internet Price field on the listing submit form.
   * Called via onchange on the toggle checkbox in member-submit-form.liquid.
   */
  window.rvlToggleInetPrice = function (checkbox) {
    var field = document.getElementById('rvl-inet-field');
    if (field) {
      field.style.display = checkbox.checked ? 'block' : 'none';
    }
  };

  /* ============================================================
     MEMBER DASHBOARD — LISTING MANAGEMENT
     Delegated from member-dashboard.liquid inline script.
     Re-declared here as a fallback for themes that load this file
     before the inline script runs.
     ============================================================ */

  /**
   * Delete a listing from localStorage by index and reload.
   */
  window.rvlDeleteListing = window.rvlDeleteListing || function (idx) {
    if (!confirm('Remove this listing?')) return;
    try {
      var listings = JSON.parse(localStorage.getItem('rvl_my_listings') || '[]');
      listings.splice(idx, 1);
      localStorage.setItem('rvl_my_listings', JSON.stringify(listings));
    } catch (e) { /* ignore */ }
    location.reload();
  };

  /**
   * Un-save a saved listing by URL and reload.
   */
  window.rvlUnsave = window.rvlUnsave || function (url) {
    try {
      var saved = JSON.parse(localStorage.getItem('rvl_saved_listings') || '[]');
      var updated = saved.filter(function (s) { return s.url !== url; });
      localStorage.setItem('rvl_saved_listings', JSON.stringify(updated));
    } catch (e) { /* ignore */ }
    location.reload();
  };

  /* ============================================================
     BOOKMARK / SAVE LISTING
     Wires up any .rvl-save-btn elements on listing cards.
     Stores { title, url, seller } to localStorage.
     ============================================================ */

  function initSaveButtons() {
    document.querySelectorAll('.rvl-save-btn').forEach(function (btn) {
      var url    = btn.dataset.url    || window.location.pathname;
      var title  = btn.dataset.title  || document.title;
      var seller = btn.dataset.seller || '';

      // Mark already-saved buttons
      var saved = getSaved();
      if (saved.some(function (s) { return s.url === url; })) {
        btn.classList.add('saved');
        btn.setAttribute('aria-pressed', 'true');
      }

      btn.addEventListener('click', function () {
        toggleSave(url, title, seller, btn);
      });
    });
  }

  function getSaved() {
    try {
      return JSON.parse(localStorage.getItem('rvl_saved_listings') || '[]');
    } catch (e) { return []; }
  }

  function toggleSave(url, title, seller, btn) {
    var saved   = getSaved();
    var already = saved.some(function (s) { return s.url === url; });

    if (already) {
      saved = saved.filter(function (s) { return s.url !== url; });
      btn.classList.remove('saved');
      btn.setAttribute('aria-pressed', 'false');
    } else {
      saved.push({ url: url, title: title, seller: seller });
      btn.classList.add('saved');
      btn.setAttribute('aria-pressed', 'true');
    }

    try {
      localStorage.setItem('rvl_saved_listings', JSON.stringify(saved));
    } catch (e) { /* quota exceeded */ }
  }

  /* ============================================================
     DETAIL PAGE — SHARE BUTTON
     Copies listing URL to clipboard with a toast notification.
     ============================================================ */

  function initShareButton() {
    var shareBtn = document.getElementById('rvl-share-btn');
    if (!shareBtn) return;

    shareBtn.addEventListener('click', function () {
      var url = window.location.href;
      if (navigator.clipboard) {
        navigator.clipboard.writeText(url).then(function () {
          showToast('Link copied to clipboard!');
        });
      } else {
        // Fallback for older browsers
        var ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity  = '0';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } catch (e) { /* ignore */ }
        document.body.removeChild(ta);
        showToast('Link copied!');
      }
    });
  }

  /* ============================================================
     TOAST NOTIFICATION
     ============================================================ */

  function showToast(message, duration) {
    duration = duration || 2500;
    var existing = document.getElementById('rvl-toast');
    if (existing) existing.remove();

    var toast = document.createElement('div');
    toast.id = 'rvl-toast';
    toast.textContent = message;
    toast.style.cssText = [
      'position:fixed',
      'bottom:24px',
      'left:50%',
      'transform:translateX(-50%)',
      'background:var(--rvl-gold, #C9A84C)',
      'color:#000',
      'font-weight:600',
      'font-size:0.875rem',
      'padding:10px 20px',
      'border-radius:6px',
      'box-shadow:0 4px 16px rgba(0,0,0,.4)',
      'z-index:9999',
      'opacity:0',
      'transition:opacity .25s'
    ].join(';');

    document.body.appendChild(toast);
    // Trigger transition
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { toast.style.opacity = '1'; });
    });

    setTimeout(function () {
      toast.style.opacity = '0';
      setTimeout(function () { toast.remove(); }, 300);
    }, duration);
  }

  /* ============================================================
     MOBILE FILTER DRAWER
     Adds a "Filters" toggle button above the grid on mobile
     if one isn't already in the Liquid template.
     ============================================================ */

  function initMobileFilterToggle() {
    // Only inject if on listings page and toggle doesn't already exist
    var existingToggle = document.getElementById('rvl-filter-toggle');
    var sidebar = document.getElementById('rvl-filters');
    var sortBar = document.querySelector('.rvl-sort-bar');

    if (!sidebar || !sortBar || existingToggle) return;

    // Only show injected toggle at mobile widths
    var btn = document.createElement('button');
    btn.id = 'rvl-filter-toggle';
    btn.className = 'rvl-btn-outline rvl-filter-toggle-btn';
    btn.textContent = '⚙ Filters';
    btn.style.display = 'none'; // Hidden by default; CSS makes it visible on mobile

    sortBar.insertBefore(btn, sortBar.firstChild);

    btn.addEventListener('click', function () {
      sidebar.classList.toggle('rvl-filters--open');
    });

    // Close sidebar when clicking outside on mobile
    document.addEventListener('click', function (e) {
      if (
        sidebar.classList.contains('rvl-filters--open') &&
        !sidebar.contains(e.target) &&
        e.target !== btn
      ) {
        sidebar.classList.remove('rvl-filters--open');
      }
    });
  }

  /* ============================================================
     HERO SEARCH — smooth scroll to first result
     ============================================================ */

  function initHeroSearch() {
    var heroSearchBtn = document.querySelector('.rvl-hero__search button');
    var heroInput     = document.getElementById('rvl-search');
    var mainSearch    = document.getElementById('rvl-search');

    if (!heroSearchBtn || !heroInput) return;

    heroSearchBtn.addEventListener('click', function () {
      // Scroll down to the grid
      var layoutEl = document.querySelector('.rvl-layout');
      if (layoutEl) {
        layoutEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    heroInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        heroSearchBtn.click();
      }
    });
  }

  /* ============================================================
     DOM READY INIT
     ============================================================ */

  function onReady(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  onReady(function () {
    initSaveButtons();
    initShareButton();
    initMobileFilterToggle();
    initHeroSearch();
  });

})();
